// F5.2-CB2 — Shared `CREATE TRIGGER … ON <table>` host-table scanner.
//
// Returns the host table whose trigger body wraps the cursor. Used by the
// PG / MySQL / SQLite resolvers so that `NEW.|` / `OLD.|` inside a trigger
// body resolves to the trigger's target-table columns rather than to any
// inner DML target (e.g. an INSERT inside the body) or to nothing.
//
// Engines and shapes covered (all share the `… TRIGGER … ON <name>` core):
//   - SQLite: `CREATE [TEMP] TRIGGER name {BEFORE|AFTER|INSTEAD OF}
//             {INSERT|UPDATE|DELETE [OF col,...]} ON <table>
//             [WHEN expr] BEGIN <body> END;`
//   - MySQL:  `CREATE [DEFINER = …] TRIGGER name {BEFORE|AFTER}
//             {INSERT|UPDATE|DELETE} ON <table> FOR EACH ROW
//             {BEGIN <body> END | <stmt>};`
//   - PG (direct form, rare): `CREATE [OR REPLACE] TRIGGER name … ON <table>
//             [FOR EACH ROW] EXECUTE FUNCTION <fn>();` — the body is the
//             function. We also surface the trigger when the cursor lies
//             inside the *function body* the trigger calls — handled via the
//             plpgsqlBlock-aware overload below.
//   - Oracle: NOT covered by this helper (uses `:NEW`/`:OLD` syntax handled
//             elsewhere if needed; out of scope for CB2).
//
// MSSQL has its own equivalent `findEnclosingTriggerHostTable` in
// `mssqlProceduralExtras.ts` that bookkeeps T-SQL specifics (`GO` batches,
// `CREATE OR ALTER`). It is intentionally NOT replaced here — leaving the
// MSSQL implementation untouched keeps that helper's behaviour stable and
// is the path-of-least-disruption per the CB2 scope.
//
// Approach: text-walk via the shared `tokenize()`. Scan from buffer start up
// to the cursor; for each `CREATE [OR REPLACE] [TEMP] TRIGGER`, jump forward
// (bounded) to find the next `ON <identifier>` and capture the target
// (schema-qualified or bare). The "active" trigger is the most recent one
// whose body still wraps the cursor — terminated by an explicit `END;`/`$$`
// closer when feasible, otherwise by the next top-level `CREATE` / `DROP`.

import type { DatabaseEngineId } from '../../../core/engines';
import { tokenize, type Token } from '../context/tokenizer';
import { parseIdentifierToken } from './identifierUtils';

export type TriggerHostTable = {
  readonly schema?: string;
  readonly name: string;
};

/**
 * Find the enclosing CREATE TRIGGER's target table for cursor at `offset`.
 * Returns null when the cursor is not inside a recognised trigger body, or
 * when the trigger statement is malformed.
 */
export function findTriggerHostTable(
  source: string,
  offset: number,
  engineId: DatabaseEngineId,
): TriggerHostTable | null {
  // Oracle and MSSQL are out of scope for this shared helper.
  if (engineId === 'oracle' || engineId === 'mssql') return null;
  const tokens = tokenize(source, 0, source.length, engineId);
  let active: TriggerHostTable | null = null;
  for (let i = 0; i < tokens.length; i += 1) {
    const tok = tokens[i];
    if (tok.start >= offset) break;
    const upper = identUpper(tok);
    if (!upper) continue;
    if (upper === 'CREATE') {
      const after = skipOrReplaceTempPreamble(tokens, i + 1);
      const kindTok = tokens[after];
      const kindUpper = identUpper(kindTok);
      if (kindUpper === 'TRIGGER') {
        const onIdx = findNextOnIdx(tokens, after + 1);
        if (onIdx === -1) continue;
        const target = readQualifiedName(tokens, onIdx + 1);
        if (!target) continue;
        active = target.target;
        i = target.lastIdx;
        continue;
      }
      // Any other top-level CREATE terminates the previous trigger's reach.
      // PG often has `CREATE OR REPLACE FUNCTION …` followed later by
      // `CREATE TRIGGER … EXECUTE FUNCTION …`, so we deliberately don't
      // null out `active` when the next token is FUNCTION — we let the
      // function-body path below tie things together.
      if (kindUpper !== 'FUNCTION' && kindUpper !== 'OR') {
        active = null;
      }
    }
  }
  return active;
}

/**
 * PG-specific overload: find the trigger that wraps the cursor by checking
 * whether the cursor lies inside a CREATE [OR REPLACE] FUNCTION body
 * (dollar-quoted) AND a downstream CREATE TRIGGER … EXECUTE FUNCTION <fn>
 * references that function. The host table is read from that trigger's
 * `ON <table>` clause.
 *
 * Returns null when no link can be established. Caller is expected to
 * fall back to the direct-form `findTriggerHostTable` first; this overload
 * handles the more common PG idiom where bodies live inside trigger
 * functions and the CREATE TRIGGER glue is a separate statement.
 */
export function findPgTriggerHostViaFunction(
  source: string,
  offset: number,
): TriggerHostTable | null {
  // Detect the enclosing CREATE [OR REPLACE] FUNCTION <name> using a
  // forward token scan: track every CREATE FUNCTION whose dollar-quoted
  // body wraps the cursor.
  const tokens = tokenize(source, 0, source.length, 'pgsql');
  const enclosingFn = findEnclosingFunctionName(tokens, source, offset);
  if (!enclosingFn) return null;
  // Now find any CREATE TRIGGER … EXECUTE [PROCEDURE|FUNCTION] <enclosingFn>(…)
  // anywhere in the buffer.
  return findTriggerByExecuteFunction(tokens, enclosingFn);
}

function findEnclosingFunctionName(
  tokens: readonly Token[],
  source: string,
  cursor: number,
): { schema?: string; name: string } | null {
  // Walk the token stream looking for `CREATE [OR REPLACE] FUNCTION
  // <qualified-name>` followed (somewhere later) by an opening dollar-quote
  // tag. Track the most-recent such pair whose body span encloses the
  // cursor. We use the dollar-quote positions in the source string for the
  // body-span check (token stream doesn't carry dollar-quote spans).
  let best: { schema?: string; name: string } | null = null;
  for (let i = 0; i < tokens.length; i += 1) {
    const tok = tokens[i];
    if (identUpper(tok) !== 'CREATE') continue;
    const after = skipOrReplaceTempPreamble(tokens, i + 1);
    if (identUpper(tokens[after]) !== 'FUNCTION') continue;
    const nameRead = readQualifiedName(tokens, after + 1);
    if (!nameRead) continue;
    // Find the opening dollar-quote tag at or after `nameRead.lastIdx`'s
    // text position.
    const startPos = tokens[nameRead.lastIdx]?.end ?? 0;
    const openInfo = findDollarOpener(source, startPos);
    if (!openInfo) continue;
    const closeIdx = findDollarCloser(source, openInfo.bodyStart, openInfo.tag);
    const bodyEnd = closeIdx === -1 ? source.length : closeIdx;
    if (cursor > openInfo.bodyStart && cursor <= bodyEnd) {
      const targetName = nameRead.target;
      best = targetName.schema ? { schema: targetName.schema, name: targetName.name } : { name: targetName.name };
    }
    i = nameRead.lastIdx;
  }
  return best;
}

function findDollarOpener(
  source: string,
  from: number,
): { tag: string; bodyStart: number } | null {
  let i = from;
  while (i < source.length) {
    if (source[i] === '$') {
      // Read a `$tag$` or `$$` opener.
      let j = i + 1;
      let tagInner = '';
      while (j < source.length) {
        const ch = source[j]!;
        if (ch === '$') {
          return { tag: `$${tagInner}$`, bodyStart: j + 1 };
        }
        if (!/[A-Za-z0-9_]/.test(ch)) break;
        tagInner += ch;
        j += 1;
      }
    }
    i += 1;
  }
  return null;
}

function findDollarCloser(source: string, from: number, tag: string): number {
  let i = from;
  while (i + tag.length <= source.length) {
    if (source.slice(i, i + tag.length) === tag) return i;
    i += 1;
  }
  return -1;
}

function findTriggerByExecuteFunction(
  tokens: readonly Token[],
  fn: { schema?: string; name: string },
): TriggerHostTable | null {
  const fnNameLower = fn.name.toLowerCase();
  const fnSchemaLower = fn.schema ? fn.schema.toLowerCase() : undefined;
  for (let i = 0; i < tokens.length; i += 1) {
    if (identUpper(tokens[i]) !== 'CREATE') continue;
    const after = skipOrReplaceTempPreamble(tokens, i + 1);
    if (identUpper(tokens[after]) !== 'TRIGGER') continue;
    const onIdx = findNextOnIdx(tokens, after + 1);
    if (onIdx === -1) continue;
    const target = readQualifiedName(tokens, onIdx + 1);
    if (!target) continue;
    // Scan forward (bounded) for `EXECUTE {PROCEDURE|FUNCTION} <name>`.
    const limit = Math.min(tokens.length, target.lastIdx + 64);
    for (let j = target.lastIdx + 1; j < limit; j += 1) {
      if (identUpper(tokens[j]) !== 'EXECUTE') continue;
      const k = j + 1;
      const next = identUpper(tokens[k]);
      if (next !== 'PROCEDURE' && next !== 'FUNCTION') continue;
      const fnRef = readQualifiedName(tokens, k + 1);
      if (!fnRef) continue;
      const refName = fnRef.target.name.toLowerCase();
      const refSchema = fnRef.target.schema ? fnRef.target.schema.toLowerCase() : undefined;
      if (refName !== fnNameLower) continue;
      if (fnSchemaLower && refSchema && fnSchemaLower !== refSchema) continue;
      return target.target;
    }
  }
  return null;
}

function skipOrReplaceTempPreamble(tokens: readonly Token[], idx: number): number {
  // Walk past `OR REPLACE`, `TEMP`, `TEMPORARY`, `OR ALTER` (T-SQL-ish).
  let cur = idx;
  // OR REPLACE
  if (identUpper(tokens[cur]) === 'OR' && (identUpper(tokens[cur + 1]) === 'REPLACE' || identUpper(tokens[cur + 1]) === 'ALTER')) {
    cur += 2;
  }
  // TEMP / TEMPORARY
  if (identUpper(tokens[cur]) === 'TEMP' || identUpper(tokens[cur]) === 'TEMPORARY') {
    cur += 1;
  }
  return cur;
}

function findNextOnIdx(tokens: readonly Token[], from: number): number {
  // Bounded forward scan. The trigger header is short — generously bounded
  // to handle `[DEFINER = `user`@`host`]` (MySQL) preambles.
  const limit = Math.min(tokens.length, from + 64);
  for (let i = from; i < limit; i += 1) {
    if (identUpper(tokens[i]) === 'ON') return i;
  }
  return -1;
}

function readQualifiedName(
  tokens: readonly Token[],
  startIdx: number,
): { target: TriggerHostTable; lastIdx: number } | null {
  if (startIdx >= tokens.length) return null;
  const head = tokens[startIdx];
  if (!head || head.type !== 'ident') return null;
  const parts: string[] = [parseIdentifierToken(head.text).content];
  let i = startIdx + 1;
  while (i < tokens.length && tokens[i].type === 'dot') {
    const next = tokens[i + 1];
    if (!next || (next.type !== 'ident' && next.type !== 'kw')) break;
    parts.push(parseIdentifierToken(next.text).content);
    i += 2;
  }
  const name = parts[parts.length - 1]!;
  const schema = parts.length >= 2 ? parts[parts.length - 2] : undefined;
  return { target: schema ? { schema, name } : { name }, lastIdx: i - 1 };
}

function identUpper(t: Token | undefined): string | null {
  if (!t) return null;
  if (t.type !== 'ident' && t.type !== 'kw') return null;
  return t.text.toUpperCase();
}
